/**
 * TtsEditDialog — edit the source text of a TTS recording and
 * re-synthesize the audio in place.
 *
 * Open from the recording detail page's "TTS script" section
 * (only rendered for TTS recordings — `source='tts'` or
 * `created_via='tts'`). On Save, calls
 * `POST /api/recordings/:id/regenerate-tts` which overwrites the
 * audio file at the existing storage key. All IVR nodes referencing
 * this recording start playing the new audio on their next call
 * with no further action.
 *
 * The recording's name / folder / ref_number / tags stay intact.
 * Only the text + audio + language + voice are updated.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recordings, type RecordingRow } from '@/api/recordings';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface Props {
  row: RecordingRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LANGUAGE_OPTIONS: Array<{ value: 'en' | 'he' | 'yi'; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'he', label: 'Hebrew' },
  { value: 'yi', label: 'Yiddish' },
];

export function TtsEditDialog({ row, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();

  // Seed from the recording's source_text. Most TTS rows have one;
  // a few legacy rows pre-0033 may not, in which case the textarea
  // opens empty and the user types in fresh script.
  const [text, setText] = useState(row.source_text ?? '');
  const [language, setLanguage] = useState<'en' | 'he' | 'yi'>(row.language ?? 'en');

  // Re-seed when the row changes (e.g. the dialog is reopened on a
  // different recording).
  useEffect(() => {
    if (open) {
      setText(row.source_text ?? '');
      setLanguage(row.language ?? 'en');
    }
  }, [open, row.id, row.source_text, row.language]);

  const regen = useMutation({
    mutationFn: () => recordings.regenerateTts(row.id, {
      text: text.trim(),
      language,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recordings', row.id] });
      qc.invalidateQueries({ queryKey: ['recordings'] });
      toast({ title: 'Audio updated', description: 'The new audio will play on the next call.' });
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Could not update';
      toast({
        title: 'Could not update audio',
        description: m,
        variant: 'destructive',
      });
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!text.trim()) {
      toast({ title: 'Text is required', variant: 'destructive' });
      return;
    }
    regen.mutate();
  };

  const dirty = text.trim() !== (row.source_text ?? '').trim() || language !== row.language;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit TTS script — {row.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="tts-edit-text">Text</Label>
            <Textarea
              id="tts-edit-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              maxLength={3000}
              placeholder="Enter the text Polly should speak…"
              autoFocus
            />
            <div className="flex justify-between text-[11px] text-muted-foreground">
              <span>The audio file will be overwritten in place — IVR nodes using this recording start playing the new audio on the next call.</span>
              <span className="font-mono tabular-nums">{text.length}/3000</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tts-edit-language">Language</Label>
            <Select value={language} onValueChange={(v) => setLanguage(v as 'en' | 'he' | 'yi')}>
              <SelectTrigger id="tts-edit-language" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Polly picks a default voice for the chosen language. The
              recording's existing voice is preserved when the language
              stays the same.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!dirty || regen.isPending}>
              {regen.isPending ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  Re-synthesizing…
                </>
              ) : (
                'Save and re-render'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
